import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  setStep,
  addStepsData,
  resetJointApplicationData,
  resetStepsData,
} from "app/lib/store/slices/steps/loanTypesSteps";
import FormGroup from "app/components/common/inputs/FormGroup";
import SelectField from "app/components/common/inputs/SelectField";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import RadioButton from "app/components/common/buttons/RadioButton";
import SubTitle from "app/components/common/typography/SubTitle";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { relactionshipOptions } from "app/utils/staticValues";

interface FormData {
  applicationType: string;
  coApplicantRelationship?: string;
  coApplicantsSameAddress?: string;
}

const TypeOfApplication: React.FC = () => {
  const dispatch = useDispatch();
  const { loanApplication, ...remaingData } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );
  const joinApplicants = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );
  const isRefinance = remaingData?.vehicleDetail?.purchaseFrom === "Refinance";
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>();

  const isjointApplication = watch("applicationType");

  const onSubmit = (data: FormData) => {
    const {
      applicationType,
      coApplicantRelationship,
      coApplicantsSameAddress,
    } = data;

    dispatch(
      addStepsData({
        ...remaingData,
        loanApplication: {
          ...loanApplication,
          applicationType: applicationType,
          ...(applicationType === "joint" && {
            coApplicantRelationship: coApplicantRelationship,
            coApplicantsSameAddress:
              coApplicantsSameAddress === "yes" ? true : false,
          }),
        },
      })
    );

    if (applicationType === "joint") {
      dispatch(setStep(isRefinance ? 19 : 18));
    } else {
      dispatch(nextStep());
    }
  };

  useEffect(() => {
    if (loanApplication) {
      reset({
        applicationType: loanApplication.applicationType,
        ...(loanApplication.applicationType === "joint" && {
          coApplicantRelationship: loanApplication?.coApplicantRelationship,
          coApplicantsSameAddress: loanApplication?.coApplicantsSameAddress
            ? "yes"
            : "no",
        }),
      });
    }
  }, [loanApplication, reset]);

  useEffect(() => {
    if (
      isjointApplication === "joint" &&
      remaingData?.applicants &&
      Object.keys(remaingData?.applicants).length > 0
    ) {
      dispatch(resetStepsData());
    } else if (
      isjointApplication === "individual" &&
      joinApplicants &&
      Object.keys(joinApplicants).length > 0
    ) {
      dispatch(resetJointApplicationData());
    }
  }, [isjointApplication]);

  return (
    <>
      <ProgressBar progress={50} />
      <div className="w-full  sm:w-[70%] xl:w-[40%]  mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Type of Application" />
        <SubTitle title="Choose an application type that suits your needs" />

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="text-left mb-5">
            <h6 className="font-Mulish text-nowrap text-base text-lightBlue mb-4">
              Select type of application?
            </h6>
            <div className="flex flex-wrap gap-4 sm:gap-5">
              <Controller
                name="applicationType"
                control={control}
                rules={{ required: "Please select type" }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex flex-wrap gap-3 sm:gap-5">
                    <RadioButton
                      id="Individual"
                      name="applicationType"
                      value="individual"
                      label="Individual"
                      checked={value === "individual"}
                      onChange={onChange}
                    />
                    <RadioButton
                      id="Jointapplication"
                      name="applicationType"
                      value="joint"
                      label="Joint application"
                      checked={value === "joint"}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
            {errors.applicationType && (
              <ErrorsMessage title={errors.applicationType.message} />
            )}
          </div>

          {isjointApplication === "joint" && (
            <>
              <FormGroup>
                <SelectField
                  label="Relationship with applicant"
                  name="coApplicantRelationship"
                  control={control}
                  options={relactionshipOptions}
                  placeholder="Please select relationship"
                  error={errors.coApplicantRelationship?.message}
                  required={true}
                  className="w-full sm:w-[49%]"
                />
              </FormGroup>
              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-lightBlue mb-4">
                  Do you both reside at the same address?
                </h6>
                <div className="flex flex-wrap gap-4 sm:gap-5">
                  <Controller
                    name="coApplicantsSameAddress"
                    control={control}
                    rules={{ required: "Please select address" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex flex-wrap gap-3 sm:gap-5">
                        <RadioButton
                          id="sameAddress_yes"
                          name="coApplicantsSameAddress"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="sameAddress_no"
                          name="coApplicantsSameAddress"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors.coApplicantsSameAddress && (
                  <ErrorsMessage
                    title={errors.coApplicantsSameAddress.message}
                  />
                )}
              </div>
            </>
          )}

          <div className="flex justify-between mt-12">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit">Next</ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default TypeOfApplication;
